import React, { useEffect, useState, useMemo, useCallback, useContext } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import siteService from '../../services/siteService';
import { Search, Plus, Edit3, ChevronDown, ChevronUp, Users, UserMinus, UserPlus, Filter, Loader } from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';
import hasUserRole from '../../utils/UserUtil';
import permissionsService from '../../services/permissionsService';

const Sites = ({ studyID }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'site_number', direction: 'ascending' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const { isAuthenticated, user } = useContext(AuthContext);
  const [filters, setFilters] = useState({
    activeParticipants: [],
    deactivatedParticipants: [],
    totalParticipants: []
  });
  const [permissions, setPermissions] = useState({
    canAddSite: false,
    canChangeSite: false
  });

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const sitesData = await siteService.getSitesForStudy(studyID);
      setSites(sitesData);
    } catch (error) {
      console.error('Error fetching data: ', error);
      setError('An error occurred while fetching the data. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [studyID]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Check permissions
  useEffect(() => {
    const checkPermissions = async () => {
      if (user) {
        try {
          const [hasAddSite, hasChangeSite] = await Promise.all([
            permissionsService.hasPermission(permissionsService.PERMISSIONS.ADD_SITE),
            permissionsService.hasPermission(permissionsService.PERMISSIONS.CHANGE_SITE)
          ]);
          setPermissions({
            canAddSite: hasAddSite,
            canChangeSite: hasChangeSite
          });
          console.log('[SITES] Permissions set:', { canAddSite: hasAddSite, canChangeSite: hasChangeSite });
        } catch (error) {
          console.error('Error checking permissions:', error);
          setPermissions({
            canAddSite: false,
            canChangeSite: false
          });
        }
      }
    };
    
    checkPermissions();
  }, [user]);

  const extractSiteNumber = (siteName) => {
    const match = siteName.match(/Site (\d+)/);
    return match ? parseInt(match[1], 10) : 0;
  };

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedSites = useMemo(() => {
    let sortableSites = [...sites];
    if (sortConfig.key) {
      sortableSites.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'site_number') {
          aValue = extractSiteNumber(a.site_name);
          bValue = extractSiteNumber(b.site_name);
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableSites;
  }, [sites, sortConfig]);

  const filteredSites = useMemo(() => {
    return sortedSites.filter(site =>
      site.site_name.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (filters.activeParticipants.length === 0 || filters.activeParticipants.includes(site.active_participants_count.toString())) &&
      (filters.deactivatedParticipants.length === 0 || filters.deactivatedParticipants.includes(site.deactivated_participants_count.toString())) &&
      (filters.totalParticipants.length === 0 || filters.totalParticipants.includes(site.total_participants_count.toString()))
    );
  }, [sortedSites, searchTerm, filters]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredSites.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const toggleFilter = (filterType, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [filterType]: prevFilters[filterType].includes(value)
        ? prevFilters[filterType].filter(item => item !== value)
        : [...prevFilters[filterType], value]
    }));
  };

  const renderFilterOptions = () => {
    const filterOptions = {
      activeParticipants: [...new Set(sites.map(site => site.active_participants_count.toString()))],
      deactivatedParticipants: [...new Set(sites.map(site => site.deactivated_participants_count.toString()))],
      totalParticipants: [...new Set(sites.map(site => site.total_participants_count.toString()))]
    };

    return (
      <div className="bg-white dark:bg-gray-800 p-4 rounded-md shadow-md dark:shadow-gray-900/20 mb-4">
        {Object.entries(filterOptions).map(([filterType, options]) => (
          <div key={filterType} className="mb-4">
            <h3 className="font-semibold mb-2 capitalize">{filterType.replace(/([A-Z])/g, ' $1').trim()}</h3>
            <div className="flex flex-wrap gap-2">
              {options.map(option => (
                <button
                  key={option}
                  onClick={() => toggleFilter(filterType, option)}
                  className={`px-3 py-1 rounded-full text-sm ${filters[filterType].includes(option)
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderTableHeader = () => (
    <thead className="bg-gray-50 dark:bg-gray-900">
      <tr>
        {[
          { key: 'site_number', label: 'Site Name' },
          { key: 'active_participants_count', label: 'Active' },
          { key: 'deactivated_participants_count', label: 'Deactivated' },
          { key: 'total_participants_count', label: 'Total' },
          { key: 'actions', label: 'Actions' }
        ].map(({ key, label }) => (
          <th
            key={key}
            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer whitespace-nowrap"
            onClick={() => requestSort(key)}
          >
            <div className="flex items-center">
              {label}
              {sortConfig.key === key && (
                sortConfig.direction === 'ascending' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
              )}
            </div>
          </th>
        ))}
      </tr>
    </thead>
  );

  const renderTableBody = () => (
    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200">
      {currentItems.map((site) => (
        <tr key={site.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
          <td className="px-6 py-4 whitespace-nowrap">
            <Link to={`/studies/${studyID}/sites/${site.id}`} className="text-primary-600 hover:text-primary-900">{site.site_name}</Link>
          </td>
          <td className="px-6 py-4 whitespace-nowrap">
            <div className="flex items-center">
              <UserPlus size={14} className="text-green-600 mr-2" />
              <span className="text-gray-900">{site.active_participants_count}</span>
            </div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap">
            <div className="flex items-center">
              <UserMinus size={14} className="text-red-600 mr-2" />
              <span className="text-gray-900">{site.deactivated_participants_count}</span>
            </div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap">
            <div className="flex items-center">
              <Users size={14} className="text-primary-600 mr-2" />
              <span className="text-gray-900">{site.total_participants_count}</span>
            </div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
          {permissions.canChangeSite && (
            <Link to={`/studies/${studyID}/sites/${site.id}/edit`} className="text-primary-600 hover:text-primary-900">
              <Edit3 size={18} />
            </Link>
          )}
          </td>
        </tr>
      ))}
    </tbody>
  );

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-primary-50 text-primary-600 rounded-lg w-9 h-9 flex items-center justify-center">
            <Users className="w-4.5 h-4.5" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 tracking-tight">Study Sites</h2>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}

        <div className="mb-6 flex justify-between items-center">
          <div className="relative">
            <input
              type="text"
              placeholder="Search sites..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary-600 focus:border-primary-600 outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          </div>
          <div className="flex space-x-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center px-4 py-2 border rounded-md text-white ${showFilters ? 'bg-primary-600 border-primary-600' : 'bg-gray-400 border-gray-400'
                } hover:bg-opacity-90`}
            >
              <Filter size={20} className="mr-2" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
            {permissions.canAddSite && (
            <button
              onClick={() => navigate(`/studies/${studyID}/add-site`)}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              <Plus size={20} className="mr-2" />
              Add New Site
            </button>
            )}
          </div>
        </div>

        {showFilters && renderFilterOptions()}

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <Loader className="animate-spin text-primary-500" size={48} />
          </div>
        ) : (
          <>
            <div className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/20 overflow-hidden sm:rounded-lg">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  {renderTableHeader()}
                  {renderTableBody()}
                </table>
              </div>
            </div>

            <div className="mt-4 flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to <span className="font-medium">{Math.min(indexOfLastItem, filteredSites.length)}</span> of{' '}
                  <span className="font-medium">{filteredSites.length}</span> results
                </p>
              </div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm dark:shadow-gray-900/20 -space-x-px" aria-label="Pagination">
                {Array.from({ length: Math.ceil(filteredSites.length / itemsPerPage) }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => paginate(index + 1)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === index + 1
                        ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                        : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                  >
                    {index + 1}
                  </button>
                ))}
              </nav>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Sites;